import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import setupPolling from 'utilities/setupPolling';

import useToggle from 'components/hooks/useModal';

import CircleQuestion from 'components/application/CircleQuestion';
import Glimmer from 'components/application/Glimmer';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';

import { sendAnalytics } from 'components/helpers/analytics';

import * as procoreAPI from '../../requests';
import InactiveCard from '../cards/InactiveCard';
import SelectedCard from '../cards/SelectedCard';
import Modal from '../modal/Modal';
import InputFields from './InputFields';
import SelectionButton from './SelectionButton';

// ************************** Top Level Component
const defaultAssignedDetails = {
  assignerID: null,
  company: null,
  folder: null,
  project: null,
};
const defaultSeedDetails = { canManage: false, loaded: false };

const pollingInterval = 1000;
const pollingMaxTime = 600000;

export default function Manager({ integration, procoreIdentityID }) {
  // state
  const breadBoard = useBreadBoard();
  const { user: currentUser } = useCurrentActor();

  const [assignedDetails, setAssignedDetails] = useState(
    defaultAssignedDetails,
  );
  const [isSelecting, setIsSelecting] = useToggle(false);
  const [seedDetails, setSeedDetails] = useState(defaultSeedDetails);
  const [unlinkedAt, setUnlinkedAt] = useState(null);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'procore.fileDestination.components.inputFieldManagement.Manager',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  // computed
  const hasProcoreIdentity = !!procoreIdentityID;
  const isSubmittable = !!(
    assignedDetails.company &&
    assignedDetails.project &&
    assignedDetails.folder
  );
  const isUpdating = !!integration;
  const isDeleting = !!(isUpdating && unlinkedAt && !isSubmittable);

  let seededLocation;
  let integrationID;

  if (integration) {
    seededLocation = {
      companyID: integration.data.relationships.procoreCompany.data.id,
      projectID: integration.data.relationships.procoreProject.data.id,
      folderID: integration.data.relationships.procoreFolder.data.id,
      assignerID: integration.data.relationships.externalIdentity.data.id,
    };
    integrationID = integration.data.id;
  }

  const status = (function () {
    switch (true) {
      case !hasProcoreIdentity:
        return seededLocation ?
            'noUserIntegerationWithSeed'
          : 'noUserIntegerationWithoutSeed';
      case seededLocation && !seedDetails.loaded:
        return 'loading';
      case seededLocation && !seedDetails.canManage:
        return 'noPermission';
      case isSubmittable:
        return 'submittable';
      default:
        return 'unselected';
    }
  })();

  // handlers

  const handleUnlink = () => {
    setUnlinkedAt(moment().unix());
    setAssignedDetails(defaultAssignedDetails);
    sendAnalytics('Procore folder unlinked', { currentUser });
  };

  const handlePollingResponseCheck = (response) =>
    ['completed', 'failed'].includes(response.data.meta.requestStatus);

  const polling = (requestFn) =>
    setupPolling({
      requestFn,
      testFn: handlePollingResponseCheck,
      intervalTime: pollingInterval,
      maxTime: pollingMaxTime,
    });

  const bouncerPolling = useMemo(
    () =>
      seededLocation &&
      polling(() =>
        procoreAPI.fetchProcorePermissions({
          companyID: seededLocation.companyID,
          projectID: seededLocation.projectID,
        }),
      ),
    [seededLocation],
  );

  const folderPolling = useMemo(
    () =>
      seededLocation &&
      polling(() =>
        procoreAPI.fetchProcoreDocument({
          companyID: seededLocation.companyID,
          folderID: seededLocation.folderID,
          projectID: seededLocation.projectID,
        }),
      ),
    [seededLocation],
  );

  const projectPolling = useMemo(
    () =>
      seededLocation &&
      polling(() =>
        procoreAPI.fetchProcoreCompanyProject({
          companyID: seededLocation.companyID,
          projectID: seededLocation.projectID,
        }),
      ),
    [seededLocation],
  );

  const fetchAllSeedRequirements = () =>
    Promise.all([
      projectPolling.startPolling(),
      folderPolling.startPolling(),
      bouncerPolling.startPolling(),
    ]);

  const handleSeedResourcesFetched = (responses) => {
    if (
      responses.every(
        (response) => response.data.meta.requestStatus === 'completed',
      )
    ) {
      const [projectsResponse, documentsResponse, bouncerResponse] = responses;

      setAssignedDetails({
        project: projectsResponse.data.data,
        folder: documentsResponse.data.data,
        // yeah I know that's cheating, don't @ me
        company: { id: seededLocation.companyID },
        assignerID: seededLocation.assignerID,
      });

      setSeedDetails({
        canManage: bouncerResponse.data.data.attributes.canManageFolders,
        loaded: true,
      });
    } else if (
      responses.some(
        (response) => response.data.meta.errorReason === 'not_found',
      )
    ) {
      setSeedDetails({ canManage: false, loaded: true });
    } else {
      breadBoard.addInedibleToast();
    }
  };

  useEffect(() => {
    if (hasProcoreIdentity && seededLocation) {
      fetchAllSeedRequirements()
        .then(handleSeedResourcesFetched)
        .catch((error) =>
          error.response.status !== 404 ?
            breadBoard.addInedibleToast()
          : (() => {
              throw error;
            })(),
        )
        .catch(() => setSeedDetails({ canManage: false, loaded: true }));
    }
  }, []);

  useEffect(
    () => () => {
      bouncerPolling.endPolling();
      folderPolling.endPolling();
      projectPolling.endPolling();
    },
    [],
  );

  const statusContent = (() => {
    switch (status) {
      case 'noUserIntegerationWithoutSeed':
        return (
          <Tooltip
            placement='top'
            tooltip={i18nT(
              'statusContent.noUserIntegerationWithoutSeed.tooltip',
            )}
            trigger='hover'
          >
            <SelectionButton disabled={true} />
          </Tooltip>
        );
      case 'noUserIntegerationWithSeed':
        return (
          <InactiveCard
            reason={i18nT('statusContent.noUserIntegerationWithSeed.reason')}
          />
        );
      case 'loading':
        return (
          <InactiveCard
            reason={
              <span>
                {i18nTShared('loading...')}
                <Glimmer />
              </span>
            }
          />
        );
      case 'noPermission':
        return (
          <InactiveCard reason={i18nT('statusContent.noPermission.reason')} />
        );
      case 'submittable':
        return (
          <SelectedCard
            folderName={assignedDetails.folder.attributes.name}
            onEdit={() => setIsSelecting(true)}
            onUnlink={handleUnlink}
            projectName={assignedDetails.project.attributes.name}
          />
        );
      case 'unselected':
        return <SelectionButton onClick={() => setIsSelecting(true)} />;
    }
  })();

  const integrationOptions =
    integrationID ?
      {
        id: integrationID,
        mode: isDeleting ? 'delete' : 'update',
      }
    : null;

  return (
    <div className='form-group'>
      <div className='tw-flex'>
        <span
          className={classNames(
            'col-sm-3 tw-text-right tw-text-m tw-font-medium tw-tracking-auto',
            isSubmittable ? 'tw-pt-[17px]' : 'tw-pt-2.5',
          )}
        >
          {i18nT('label')}
        </span>
        {hasProcoreIdentity && (
          <InputFields
            {...{
              procoreIdentityID,
              assignedDetails,
              existingIntegration: integrationOptions,
            }}
          />
        )}
        <div className='col-sm-8 tw-flex'>
          {statusContent}
          <div className='tooltip-parent tw-ml-3 tw-mt-2.5'>
            <CircleQuestion />
            <Tooltip
              placement='top'
              tooltip={i18nT('statusContent.tooltip')}
              trigger='hover'
            />
          </div>
        </div>
      </div>
      <Modal
        currentUser={currentUser}
        isSelecting={isSelecting}
        key={`locationSelectorModal--${unlinkedAt}`}
        onCancel={() => setIsSelecting(false)}
        onSelection={({ company, folder, project }) => {
          setAssignedDetails({
            company,
            project,
            folder,
            loaded: true,
            assignerID: procoreIdentityID,
          });

          sendAnalytics('Procore folder selected for a project', {
            currentUser,
          });

          setIsSelecting(false);
        }}
        seededLocation={unlinkedAt ? null : seededLocation}
      />
    </div>
  );
}
